import { makeAutoObservable, runInAction } from "mobx";
import { THROW_ON_ERROR } from "shared/dialogSubmit";
import type RootStore from "stores/RootStore";
import { isOperator } from "auth/tier";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import type { DevOpsCancelBuildPayload, DevOpsQueueBuildPayload } from "../devops.types";
import { subscribeDevOpsOverviewEvents } from "../DevOpsOverview/devopsOverviewLive";
import { swallowedFindMessage, swallowedSessionEnded } from "../shared/swallowedFind";
import {
  countLanes,
  fixedBy,
  groupBuilds,
  matchesLane,
  matchesSearch,
  sectionOf,
  type BuildBoardWindow,
  type BuildGroup,
  type CategoryFilter,
  type GroupBy,
  type LaneCounts,
  type LaneFilter,
} from "./buildBoard.helpers";
import type {
  BuildBoardBuild,
  BuildBoardClientRef,
  BuildBoardResponse,
  BuildBoardUnreadable,
} from "./buildBoard.types";
import { isSessionEnded } from "stores/sessionGuard";

/** How long after the last live `devops-event` the board is re-read. */
export const LIVE_REFRESH_DEBOUNCE_MS = 2000;
/**
 * The board also re-reads on a timer: a project with no webhook subscription
 * sends no events, and "currently building" must not depend on one existing.
 */
export const POLL_INTERVAL_MS = 60_000;
/** How often relative times ("3m ago", "running 4m") are re-rendered. */
export const CLOCK_TICK_MS = 15_000;
/** Finished builds shown per group before "Show N more". */
export const COLLAPSED_FINISHED_ROWS = 3;

export const DEFAULT_WINDOW: BuildBoardWindow = 24;

const PREFS_KEY = "hq.pipelinesBoard.prefs";

function unwrapBoard(result: unknown): BuildBoardResponse | null {
  const isBoard = (v: unknown): v is BuildBoardResponse =>
    !!v && typeof v === "object" && Array.isArray((v as BuildBoardResponse).builds);
  if (isBoard(result)) return result;
  const data = (result as { data?: unknown } | null | undefined)?.data;
  if (Array.isArray(data) && isBoard(data[0])) return data[0];
  return null;
}

export interface BoardSection {
  category: "client" | "internal";
  title: string;
  groups: BuildGroup[];
}

/**
 * Route ViewStore for the all-projects Pipelines board (`/devops/pipelines`).
 *
 * Plain class with `makeAutoObservable` (app store `buildBoardStore`). Reads
 * the find-only `devops-build-board` ServiceStore and holds its RESPONSE — one
 * composite per call, the same documented exception DevOpsOverviewStore uses.
 * Every filter (lane, client/internal, search, grouping) is client-side over
 * that one answer; only the window is a server parameter.
 *
 * Project 360's Pipelines TAB is a different surface — one project's build
 * DEFINITIONS — and keeps `PipelinesBoardStore`.
 *
 * LIVE: any `devops-event` re-reads 2 s after the last one (the API drops its
 * cache on the same event), plus a 60 s poll so a project without a webhook
 * subscription is still current.
 *
 * HONESTY: a null find is a FAILED read (the ServiceStore swallows rejections),
 * never an idle org. Projects the API could not read come back in
 * `unreadable` and the page says so — a project missing from the board is not
 * shown as quiet.
 */
class BuildBoardStore {
  rootStore: RootStore;

  board: BuildBoardResponse | null = null;
  loading = false;
  error: string | null = null;
  lastUpdatedAt: Date | null = null;

  windowHours: BuildBoardWindow = DEFAULT_WINDOW;
  laneFilter: LaneFilter = "all";
  categoryFilter: CategoryFilter = "all";
  groupBy: GroupBy = "client";
  search = "";

  /** Group keys whose finished builds are expanded past COLLAPSED_FINISHED_ROWS. */
  expandedGroups: string[] = [];

  /** Build keys with a cancel / re-run in flight. */
  actioningKeys: string[] = [];
  actionError: string | null = null;

  /** Ticks every CLOCK_TICK_MS so relative times re-render. */
  now = Date.now();

  private loadSeq = 0;
  private unsubscribeLive: (() => void) | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private clockTimer: ReturnType<typeof setInterval> | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
    this.restorePrefs();
  }

  // ---- Collaborators ------------------------------------------------------

  private get boardService(): ServiceStoreType | null {
    return (
      (this.rootStore.serviceStoreManager?.getStore("devops-build-board") as unknown as
        | ServiceStoreType
        | null
        | undefined) ?? null
    );
  }

  private get pipelinesService(): ServiceStoreType | null {
    return (this.rootStore.devopsPipelinesStore as ServiceStoreType | null) ?? null;
  }

  // ---- Derived ------------------------------------------------------------

  /** Whether the user may cancel / re-run builds (operator+; the API re-checks). */
  get canManage(): boolean {
    return isOperator(this.rootStore);
  }

  get builds(): BuildBoardBuild[] {
    return this.board?.builds ?? [];
  }

  get unreadable(): BuildBoardUnreadable[] {
    return this.board?.unreadable ?? [];
  }

  get hasConnections(): boolean {
    return (this.board?.connections.length ?? 0) > 0;
  }

  private get clientById(): Map<string, BuildBoardClientRef> {
    return new Map((this.board?.clients ?? []).map((c) => [c.id, c]));
  }

  clientName(build: BuildBoardBuild): string | null {
    return build.clientId ? (this.clientById.get(build.clientId)?.name ?? null) : null;
  }

  /** Failed build key → the later success that fixed it. Over ALL builds, not the filtered ones. */
  get fixes(): Map<string, BuildBoardBuild> {
    return fixedBy(this.builds);
  }

  /** Builds after category + search, BEFORE the lane filter — what the lane cards count. */
  private get scopedBuilds(): BuildBoardBuild[] {
    return this.builds.filter(
      (b) =>
        (this.categoryFilter === "all" || b.category === this.categoryFilter) &&
        matchesSearch(b, this.search, this.clientName(b)),
    );
  }

  /** The lane cards: counted over the category + search scope, so a card click never empties the page. */
  get counts(): LaneCounts {
    return countLanes(this.scopedBuilds, this.fixes);
  }

  get visibleBuilds(): BuildBoardBuild[] {
    return this.scopedBuilds.filter((b) => matchesLane(b, this.laneFilter, this.fixes));
  }

  get groups(): BuildGroup[] {
    return groupBuilds({
      builds: this.visibleBuilds,
      projects: this.board?.projects ?? [],
      clients: this.board?.clients ?? [],
      fixes: this.fixes,
      groupBy: this.groupBy,
    });
  }

  /** Clients' work first, then IntraSync's own — the two halves the page is split into. */
  get sections(): BoardSection[] {
    const out: BoardSection[] = [];
    const client = this.groups.filter((g) => sectionOf(g) === "client");
    const internal = this.groups.filter((g) => sectionOf(g) === "internal");
    if (client.length > 0) out.push({ category: "client", title: "Clients", groups: client });
    if (internal.length > 0) out.push({ category: "internal", title: "Internal", groups: internal });
    return out;
  }

  /** Projects the API read that had nothing in the window — said once, not drawn as empty cards. */
  get idleProjectCount(): number {
    const active = new Set(this.builds.map((b) => b.projectKey));
    return (this.board?.projects ?? []).filter(
      (p) =>
        !active.has(p.key) && (this.categoryFilter === "all" || p.category === this.categoryFilter),
    ).length;
  }

  get truncatedProjects(): string[] {
    return (this.board?.projects ?? []).filter((p) => p.truncated).map((p) => p.name);
  }

  get filtersActive(): boolean {
    return this.laneFilter !== "all" || this.categoryFilter !== "all" || this.search.trim() !== "";
  }

  isExpanded(groupKey: string): boolean {
    return this.expandedGroups.includes(groupKey);
  }

  isActioning(buildKey: string): boolean {
    return this.actioningKeys.includes(buildKey);
  }

  // ---- Filters ------------------------------------------------------------

  /** A lane card click: the same lane again returns to everything. */
  toggleLane(lane: LaneFilter) {
    this.laneFilter = this.laneFilter === lane ? "all" : lane;
  }

  setLaneFilter(lane: LaneFilter) {
    this.laneFilter = lane;
  }

  setCategoryFilter(category: CategoryFilter) {
    this.categoryFilter = category;
    this.savePrefs();
  }

  setGroupBy(groupBy: GroupBy) {
    this.groupBy = groupBy;
    this.expandedGroups = [];
    this.savePrefs();
  }

  setSearch(q: string) {
    this.search = q;
  }

  clearFilters() {
    this.laneFilter = "all";
    this.categoryFilter = "all";
    this.search = "";
    this.savePrefs();
  }

  toggleExpanded(groupKey: string) {
    this.expandedGroups = this.isExpanded(groupKey)
      ? this.expandedGroups.filter((k) => k !== groupKey)
      : [...this.expandedGroups, groupKey];
  }

  /** The window is a SERVER parameter — a change re-reads the board. */
  async setWindow(hours: BuildBoardWindow) {
    if (hours === this.windowHours) return;
    this.windowHours = hours;
    this.savePrefs();
    await this.load();
  }

  // ---- Loading ------------------------------------------------------------

  /** Route onEnter: subscribe, start the timers, load. */
  async initialize() {
    this.startLive();
    await this.load();
  }

  /** `fresh` asks the API to skip its short cache (the header Refresh). */
  async load(fresh = false) {
    const seq = ++this.loadSeq;
    this.loading = true;
    try {
      const service = this.boardService;
      const query: Record<string, unknown> = { windowHours: this.windowHours };
      if (fresh) query.fresh = true;
      const result = await service?.find({ query });
      if (seq !== this.loadSeq) return;

      const board = unwrapBoard(result);
      if (!board) {
        // Not a failure if a sign-out ended the call. reset() bumps loadSeq, so
        // the check above already returns for that; this keeps the branch
        // right without leaning on it.
        if (swallowedSessionEnded(service)) return;
        // Swallowed rejection — a FAILED read, never an idle org. Keep the
        // previous board on screen and say it is stale.
        runInAction(() => {
          this.error = swallowedFindMessage(service) ?? "Failed to load the pipelines board";
        });
        return;
      }
      runInAction(() => {
        this.board = board;
        this.error = null;
        this.lastUpdatedAt = new Date();
        this.now = Date.now();
      });
    } catch (err: unknown) {
      if (seq !== this.loadSeq) return;
      runInAction(() => {
        if (!isSessionEnded(err)) this.error = err instanceof Error ? err.message : "Failed to load the pipelines board";
      });
      console.error("Failed to load the pipelines board:", err);
    } finally {
      runInAction(() => {
        if (seq === this.loadSeq) this.loading = false;
      });
    }
  }

  /** Header refresh. */
  async reload() {
    await this.load(true);
  }

  // ---- Actions (operator+) ------------------------------------------------

  private begin(key: string) {
    if (!this.actioningKeys.includes(key)) this.actioningKeys = [...this.actioningKeys, key];
    this.actionError = null;
  }

  private end(key: string) {
    this.actioningKeys = this.actioningKeys.filter((k) => k !== key);
  }

  /** Cancel a building or queued build. The page confirms first. */
  async cancelBuild(build: BuildBoardBuild): Promise<void> {
    this.begin(build.key);
    try {
      const payload: DevOpsCancelBuildPayload = {
        action: "cancelBuild",
        connectionId: build.connectionId,
        project: build.projectId,
        buildId: build.buildId,
      };
      // THROW_ON_ERROR: a refusal otherwise RESOLVES null and read as done.
      await this.pipelinesService?.create(payload as never, THROW_ON_ERROR);
      await this.load(true);
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.actionError = err instanceof Error ? err.message : "Failed to cancel the build";
      });
      console.error("cancelBuild failed:", err);
    } finally {
      runInAction(() => this.end(build.key));
    }
  }

  /** Queue the same pipeline again on the same branch. */
  async rerunBuild(build: BuildBoardBuild): Promise<void> {
    if (build.definitionId === null) return;
    this.begin(build.key);
    try {
      const payload: DevOpsQueueBuildPayload = {
        action: "queueBuild",
        connectionId: build.connectionId,
        project: build.projectId,
        definitionId: build.definitionId,
        sourceBranch: build.sourceBranch ?? undefined,
      };
      // THROW_ON_ERROR: a refusal otherwise RESOLVES null and read as done.
      await this.pipelinesService?.create(payload as never, THROW_ON_ERROR);
      await this.load(true);
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.actionError = err instanceof Error ? err.message : "Failed to queue the build";
      });
      console.error("rerunBuild failed:", err);
    } finally {
      runInAction(() => this.end(build.key));
    }
  }

  // ---- Live ---------------------------------------------------------------

  startLive() {
    if (!this.unsubscribeLive) {
      this.unsubscribeLive = subscribeDevOpsOverviewEvents(this.rootStore, () => this.scheduleRefresh());
    }
    if (!this.pollTimer) {
      this.pollTimer = setInterval(() => void this.load(), POLL_INTERVAL_MS);
    }
    if (!this.clockTimer) {
      this.clockTimer = setInterval(() => runInAction(() => (this.now = Date.now())), CLOCK_TICK_MS);
    }
  }

  /** Coalesce a burst of events into one re-read, 2 s after the last. */
  scheduleRefresh() {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => {
      runInAction(() => {
        this.refreshTimer = null;
      });
      void this.load();
    }, LIVE_REFRESH_DEBOUNCE_MS);
  }

  /** Route onExit. */
  dispose() {
    this.unsubscribeLive?.();
    this.unsubscribeLive = null;
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
    if (this.pollTimer) clearInterval(this.pollTimer);
    this.pollTimer = null;
    if (this.clockTimer) clearInterval(this.clockTimer);
    this.clockTimer = null;
  }

  // ---- Per-viewer preferences (best-effort) -------------------------------

  private restorePrefs() {
    try {
      const raw = globalThis.localStorage?.getItem(PREFS_KEY);
      if (!raw) return;
      const p = JSON.parse(raw) as Partial<{ groupBy: GroupBy; categoryFilter: CategoryFilter; windowHours: BuildBoardWindow }>;
      if (p.groupBy === "client" || p.groupBy === "project") this.groupBy = p.groupBy;
      if (p.categoryFilter === "all" || p.categoryFilter === "client" || p.categoryFilter === "internal") {
        this.categoryFilter = p.categoryFilter;
      }
      if (p.windowHours === 6 || p.windowHours === 24 || p.windowHours === 72 || p.windowHours === 168) {
        this.windowHours = p.windowHours;
      }
    } catch {
      // storage unavailable — defaults stand
    }
  }

  private savePrefs() {
    try {
      globalThis.localStorage?.setItem(
        PREFS_KEY,
        JSON.stringify({ groupBy: this.groupBy, categoryFilter: this.categoryFilter, windowHours: this.windowHours }),
      );
    } catch {
      // storage unavailable — the choice lasts this session only
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.dispose();
    this.board = null;
    this.loading = false;
    this.error = null;
    this.lastUpdatedAt = null;
    this.windowHours = DEFAULT_WINDOW;
    this.laneFilter = "all";
    this.categoryFilter = "all";
    this.groupBy = "client";
    this.search = "";
    this.expandedGroups = [];
    this.actioningKeys = [];
    this.actionError = null;
    this.loadSeq += 1;
    this.unsubscribeLive = null;
    this.refreshTimer = null;
    this.pollTimer = null;
    this.clockTimer = null;
    // Per-browser view preferences, not the person's: back to what this browser saved.
    this.restorePrefs();
  }
}

export default BuildBoardStore;
